// Short IDs in tables: "..." and the last 4 characters (7 characters, fixed width in DM Mono). The full ID is in
// the title (mouse), in a focus tooltip (keyboard) and in the aria-label (screen readers). A copy button copies it.
import { useEffect, useState, type ReactNode } from "react";

/** "..." and the last 4 characters of an ID; an ID of 7 characters or fewer stays as it is. */
export function shortId(id: string): string {
  return id.length <= 7 ? id : `...${id.slice(-4)}`;
}

/** A short ID with its copy button, for use inside any cell. */
export function Id({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <span className="id-short">
      <code className="id-text" tabIndex={0} title={id} aria-label={id} data-full={id}>
        {shortId(id)}
      </code>
      <button
        type="button"
        className="icon-button"
        aria-label="Copy ID"
        title="Copy ID"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(id);
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
      {/* aria-live, not role="status": a page has one status region (the toast); this only announces "Copied". */}
      <span className="id-copied" aria-live="polite">
        {copied ? "Copied" : ""}
      </span>
    </span>
  );
}

/** A table cell with a short ID. `label` is the stacked-row label on narrow screens. */
export function IdCell({ id, label = "ID", children }: { id: string; label?: string; children?: ReactNode }) {
  return (
    <td className="id-cell" data-label={label}>
      {children}
      <Id id={id} />
    </td>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M11 3.5V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
